/**
 * Project Details saves send only what the PM actually changed.
 *
 * The card built the whole form as a PATCH body on every save — sixteen fields
 * whether one moved or none did. Two costs: the server re-validates and
 * re-writes columns nobody touched, and a save the PM made to fix a typo in an
 * email address is indistinguishable on the wire from one that re-asserts every
 * rate on the job. A diff makes the request say what happened.
 *
 * An empty diff is the interesting case: the editor closes and nothing is sent,
 * so "Edit project" → "Save changes" with no edits stops stamping `updated_at`
 * and stops asking the PM to confirm a blast radius of nothing.
 */
import { PROJECT_PATCH_FIELD_PAIRS } from '@coffman-tm/shared';
import type { ProjectPatchRequest, SettingsProject } from '@coffman-tm/shared';

/**
 * Every field the form can send must exist on the stored row, or the diff
 * compares it against `undefined` and reports it changed on every single save.
 * This fails to compile the moment a request field has no counterpart to read.
 */
type UncomparableField = Exclude<keyof ProjectPatchRequest, keyof SettingsProject>;
const _everyFormFieldIsOnTheStoredRow: UncomparableField extends never ? true : never = true;
void _everyFormFieldIsOnTheStoredRow;

/**
 * The recipient roster is an array, so identity comparison would report every
 * save as a change. Element-wise is enough — the only array on the body is a
 * list of strings.
 */
function sameValue(before: unknown, next: unknown): boolean {
  if (Array.isArray(before) && Array.isArray(next)) {
    return before.length === next.length && before.every((v, i) => v === next[i]);
  }
  return before === next;
}

/**
 * Fields that only mean anything as a set travel as a set.
 *
 * Flipping the Sunday toggle on a job whose stored rate already matches the
 * form diffs to the toggle ALONE — and the server needs both halves for the
 * premium to apply, so the PM would be told it is on while Sunday hours billed
 * the ordinary rate.
 */
function withPairedFields(
  patch: Record<string, unknown>,
  form: ProjectPatchRequest,
): Record<string, unknown> {
  const out = { ...patch };
  const source = form as unknown as Record<string, unknown>;
  for (const pair of PROJECT_PATCH_FIELD_PAIRS) {
    if (!pair.some((field) => field in out)) continue;
    for (const field of pair) {
      if (field in out) continue;
      const value = source[field];
      // A field the form did not send has no value to complete the pair with.
      if (value !== undefined) out[field] = value;
    }
  }
  return out;
}

/** Only the fields whose form value differs from the stored row — plus the
 *  other half of any paired field among them. */
export function diffProjectPatch(
  before: SettingsProject,
  form: ProjectPatchRequest,
): ProjectPatchRequest {
  const stored = before as unknown as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(form)) {
    // An absent key is "leave it alone" on the wire, so it cannot be a change.
    if (value === undefined) continue;
    if (!sameValue(stored[key], value)) patch[key] = value;
  }
  return withPairedFields(patch, form) as ProjectPatchRequest;
}
